import { useState } from 'react'
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  DatePicker,
  Form,
  Grid,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  CloudServerOutlined,
  EyeOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  RetweetOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import dayjs, { type Dayjs } from 'dayjs'

import apiClient from '../../api/client'
import DataTable from '../../components/DataTable'
import PageHeader from '../../components/PageHeader'

interface Service {
  subscription_id: number
  product_id: number
  product_name?: string
  identifier?: string
  kind?: string
  status: string
  billing_cycle: string
  unit_price: string
  currency: string
  next_due_date: string | null
  allow_plan_change?: boolean
  allow_pause?: boolean
  paused_at?: string | null
  pause_until?: string | null
  service?: {
    state: string
    external_ref: string | null
    last_action_at: string | null
  }
  license?: {
    id: number
    key_number: string
    bind_ip: string
    label: string
  }
}

interface CancelRequest {
  id: number
  subscription_id: number
  status: string
  request_type: string
}

interface CatalogProduct {
  id: number
  name: string
  currency: string
  pricing: Record<string, number | string> | null
  active: boolean
}

interface ChangePlanPreview {
  old_product_id: number
  old_billing_cycle: string
  old_unit_price: string
  new_product_id: number
  new_billing_cycle: string
  new_unit_price: string
  new_next_due_date: string
  prorata_credit: string
  currency: string
  days_remaining: number
  cycle_days: number
  applied: boolean
}

export default function PortalServices() {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()
  const screens = Grid.useBreakpoint()
  const [pauseTarget, setPauseTarget] = useState<Service | null>(null)
  const [pauseUntil, setPauseUntil] = useState<Dayjs | null>(null)
  const [cancelTarget, setCancelTarget] = useState<Service | null>(null)
  const [cancelForm] = Form.useForm<{ request_type: string; reason: string }>()
  const [changePlanTarget, setChangePlanTarget] = useState<Service | null>(null)
  const [cpProductID, setCpProductID] = useState<number | null>(null)
  const [cpCycle, setCpCycle] = useState<string>('')
  const [cpPreview, setCpPreview] = useState<ChangePlanPreview | null>(null)
  const [cpPreviewLoading, setCpPreviewLoading] = useState(false)
  const [catFilter, setCatFilter] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['portal-services-page'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: Service[] }>('/api/v1/portal/services')
      return res.data.data
    },
  })

  // Customer-editable license label (e.g. their server name) for fast lookup.
  const setLabel = useMutation({
    mutationFn: async ({ id, label }: { id: number; label: string }) => {
      await apiClient.patch(`/api/v1/portal/licenses/${id}/label`, { label })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-services-page'] }),
  })

  const pause = useMutation({
    mutationFn: async () => {
      if (!pauseTarget) return
      await apiClient.post(
        `/api/v1/portal/subscriptions/${pauseTarget.subscription_id}/pause`,
        { pause_until: pauseUntil ? pauseUntil.format('YYYY-MM-DD') : undefined },
      )
    },
    onSuccess: () => {
      message.success(t('portal.services.pauseOk', 'Vertrag pausiert'))
      setPauseTarget(null)
      setPauseUntil(null)
      qc.invalidateQueries({ queryKey: ['portal-services-page'] })
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: string } } }
      message.error(err.response?.data?.error
        || t('portal.services.pauseFailed', 'Pausieren fehlgeschlagen'))
    },
  })

  const unpause = useMutation({
    mutationFn: async (subID: number) => {
      await apiClient.post(`/api/v1/portal/subscriptions/${subID}/unpause`)
    },
    onSuccess: () => {
      message.success(t('portal.services.unpauseOk', 'Pause beendet'))
      qc.invalidateQueries({ queryKey: ['portal-services-page'] })
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: string } } }
      message.error(err.response?.data?.error
        || t('portal.services.unpauseFailed', 'Fortsetzen fehlgeschlagen'))
    },
  })

  const { data: catalog } = useQuery({
    queryKey: ['portal-catalog'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: CatalogProduct[] }>('/api/v1/portal/catalog/products')
      return res.data.data
    },
    enabled: !!changePlanTarget,
  })

  const fetchPreview = async (subID: number, productID: number, cycle: string) => {
    if (!productID || !cycle) { setCpPreview(null); return }
    setCpPreviewLoading(true)
    try {
      const res = await apiClient.post<{ data: ChangePlanPreview }>(
        `/api/v1/portal/subscriptions/${subID}/change-plan/preview`,
        { new_product_id: productID, new_billing_cycle: cycle },
      )
      setCpPreview(res.data.data)
    } catch {
      setCpPreview(null)
    } finally {
      setCpPreviewLoading(false)
    }
  }

  const applyPlanChange = useMutation({
    mutationFn: async () => {
      if (!changePlanTarget || !cpProductID || !cpCycle) return
      await apiClient.post(
        `/api/v1/portal/subscriptions/${changePlanTarget.subscription_id}/change-plan`,
        { new_product_id: cpProductID, new_billing_cycle: cpCycle },
      )
    },
    onSuccess: () => {
      message.success(t('portal.services.planChangeOk', 'Tarif erfolgreich gewechselt'))
      setChangePlanTarget(null)
      setCpProductID(null)
      setCpCycle('')
      setCpPreview(null)
      qc.invalidateQueries({ queryKey: ['portal-services-page'] })
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: string } } }
      message.error(err.response?.data?.error
        || t('portal.services.planChangeFailed', 'Tarifwechsel fehlgeschlagen'))
    },
  })

  const { data: cancelRequests } = useQuery({
    queryKey: ['portal-cancel-requests'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: CancelRequest[] }>('/api/v1/portal/cancel-requests')
      return res.data.data
    },
  })
  const pendingCancels = new Set(
    (cancelRequests || [])
      .filter((r) => r.status === 'pending')
      .map((r) => r.subscription_id),
  )

  const submitCancel = useMutation({
    mutationFn: async (vals: { request_type: string; reason: string }) => {
      if (!cancelTarget) return
      await apiClient.post('/api/v1/portal/cancel-requests', {
        subscription_id: cancelTarget.subscription_id,
        request_type: vals.request_type,
        reason: vals.reason || undefined,
      })
    },
    onSuccess: () => {
      message.success(t('portal.services.cancelSubmitted', 'Kündigungsanfrage eingereicht'))
      setCancelTarget(null)
      cancelForm.resetFields()
      qc.invalidateQueries({ queryKey: ['portal-cancel-requests'] })
      qc.invalidateQueries({ queryKey: ['portal-services-page'] })
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: string } } }
      message.error(err.response?.data?.error
        || t('portal.services.cancelFailed', 'Kündigung fehlgeschlagen'))
    },
  })

  const cycleLabel = (cycle: string) => {
    const map: Record<string, string> = {
      per_minute: '/Min.', hourly: '/Std.', daily: '/Tag', weekly: '/Woche',
      monthly: '/Monat', quarterly: '/Quartal', yearly: '/Jahr',
      biennial: '/2 J.', triennial: '/3 J.', quadrennial: '/4 J.',
      quinquennial: '/5 J.', sexennial: '/6 J.', one_time: 'einmalig',
    }
    return map[cycle] || `/${cycle}`
  }

  const statusTag = (r: Service) => {
    const state = r.service?.state || r.status
    const colors: Record<string, string> = {
      active: 'success', suspended: 'error', paused: 'warning',
      cancelled: 'default', pending: 'processing', terminating: 'orange',
    }
    const labels: Record<string, string> = {
      active: t('portalServices.statusActive', 'Aktiv'),
      suspended: t('portalServices.statusSuspended', 'Gesperrt'),
      paused: t('portalServices.statusPaused', 'Pausiert'),
      cancelled: t('portalServices.statusCancelled', 'Gekündigt'),
      pending: t('portalServices.statusPending', 'Ausstehend'),
      terminating: t('portalServices.statusTerminating', 'Kündigung'),
    }
    return <Tag color={colors[state] || 'default'}>{labels[state] || state}</Tag>
  }

  const renderActions = (r: Service) => (
    <Space size={4} wrap>
      <Link to={`/portal/services/${r.subscription_id}`}>
        <Button size="small" type="primary" icon={<EyeOutlined />}>
          {t('portal.services.details', 'Details')}
        </Button>
      </Link>
      {r.status === 'active' && (
        <>
          {(r.allow_plan_change ?? true) && (
            <Button size="small" icon={<RetweetOutlined />}
              onClick={() => { setChangePlanTarget(r); setCpProductID(null); setCpCycle(''); setCpPreview(null) }}>
              {t('portal.services.changePlan', 'Tarif ändern')}
            </Button>
          )}
          {(r.allow_pause ?? true) && (
            <Button size="small" icon={<PauseCircleOutlined />}
              onClick={() => setPauseTarget(r)}>
              {t('portal.services.pause', 'Pausieren')}
            </Button>
          )}
          {!pendingCancels.has(r.subscription_id) && (
            <Button size="small" danger icon={<StopOutlined />}
              onClick={() => { setCancelTarget(r); cancelForm.setFieldsValue({ request_type: 'at_period_end', reason: '' }) }}>
              {t('portal.services.cancel', 'Kündigen')}
            </Button>
          )}
        </>
      )}
      {r.status === 'paused' && (
        <Popconfirm
          title={t('portal.services.unpauseConfirm', 'Pause beenden und Vertrag wieder aktivieren?')}
          onConfirm={() => unpause.mutate(r.subscription_id)}>
          <Button size="small" type="primary" icon={<PlayCircleOutlined />}
            loading={unpause.isPending}>
            {t('portal.services.unpause', 'Pause beenden')}
          </Button>
        </Popconfirm>
      )}
    </Space>
  )

  const columns: ColumnsType<Service> = [
    {
      title: t('portal.services.columns.product', 'Produkt'),
      key: 'product',
      render: (_: unknown, r) => (
        <Space direction="vertical" size={0}>
          <Link to={`/portal/services/${r.subscription_id}`} className="font-medium">
            {r.product_name || `Service #${r.subscription_id}`}
          </Link>
          {r.identifier && (
            <Typography.Text type="secondary" className="text-xs">{r.identifier}</Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: t('portal.services.columns.license', 'Lizenz'),
      key: 'license',
      render: (_: unknown, r) => r.license ? (
        <Space direction="vertical" size={0}>
          <Typography.Text code copyable={{ text: r.license.key_number }} className="text-xs">
            {r.license.key_number}
          </Typography.Text>
          {r.license.bind_ip && (
            <Typography.Text type="secondary" className="text-xs">
              {t('portal.services.bindIp', 'Bind-IP')}: {r.license.bind_ip}
            </Typography.Text>
          )}
          <Typography.Text
            className="text-xs"
            type={r.license.label ? undefined : 'secondary'}
            editable={{
              onChange: (v) => { if (r.license) setLabel.mutate({ id: r.license.id, label: v }) },
              tooltip: t('portal.services.labelHint', 'Eigenes Label (z. B. Servername)'),
            }}
          >
            {r.license.label || t('portal.services.labelPlaceholder', '+ Label (z. B. Servername)')}
          </Typography.Text>
        </Space>
      ) : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: t('portal.services.columns.price', 'Preis'),
      key: 'price',
      width: 140,
      render: (_: unknown, r) => (
        <Typography.Text>
          {parseFloat(r.unit_price).toFixed(2)} {r.currency}
          <Typography.Text type="secondary" className="text-xs">
            {cycleLabel(r.billing_cycle)}
          </Typography.Text>
        </Typography.Text>
      ),
    },
    {
      title: t('portal.services.columns.nextDue', 'Nächste Fälligkeit'),
      dataIndex: 'next_due_date',
      key: 'next_due_date',
      width: 120,
      render: (v: string | null) => (v ? new Date(v).toLocaleDateString('de-DE') : '—'),
    },
    {
      title: t('portal.services.columns.status', 'Status'),
      key: 'status',
      width: 140,
      render: (_: unknown, r) => (
        <Space direction="vertical" size={2}>
          {statusTag(r)}
          {r.status === 'paused' && r.pause_until && (
            <Typography.Text type="secondary" className="text-xs">
              {t('portalServices.pausedUntilPrefix', 'bis')} {new Date(r.pause_until).toLocaleDateString('de-DE')}
            </Typography.Text>
          )}
          {pendingCancels.has(r.subscription_id) && (
            <Tag color="orange" className="text-xs">{t('portalServices.cancelPending', 'Kündigung ausstehend')}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 240,
      align: 'right' as const,
      render: (_: unknown, r) => renderActions(r),
    },
  ]

  const renderMobileCard = (r: Service) => (
    <div className="mob-card">
      <div className="flex-between items-start mb-10">
        <div>
          <Typography.Text strong className="text-14 d-block">
            {r.product_name || `Service #${r.subscription_id}`}
          </Typography.Text>
          {r.identifier && (
            <Typography.Text type="secondary" className="text-xs d-block">{r.identifier}</Typography.Text>
          )}
          <Typography.Text type="secondary" className="text-sm">
            {parseFloat(r.unit_price).toFixed(2)} {r.currency}{cycleLabel(r.billing_cycle)}
          </Typography.Text>
        </div>
        {statusTag(r)}
      </div>
      {r.next_due_date && (
        <Typography.Text type="secondary" className="text-sm d-block mb-10">
          {t('portalServices.nextDueLabel', 'Nächste Fälligkeit')}: {new Date(r.next_due_date).toLocaleDateString('de-DE')}
        </Typography.Text>
      )}
      {pendingCancels.has(r.subscription_id) && (
        <Tag color="orange" className="mb-10">{t('portalServices.cancelPending', 'Kündigung ausstehend')}</Tag>
      )}
      {renderActions(r)}
    </div>
  )

  return (
    <>
      <PageHeader
        icon={<CloudServerOutlined />}
        title={t('portal.services.title', 'Meine Services')}
        subtitle={t('portal.services.subtitle', 'Ihre aktiven Dienste und Abonnements.')}
      />
      {(() => {
        const services = data || []
        const getState = (r: Service) => r.service?.state || r.status
        const active = services.filter(r => getState(r) === 'active')
        const paused = services.filter(r => getState(r) === 'paused' || r.paused_at)
        const suspended = services.filter(r => getState(r) === 'suspended')
        return (
          <div className="kpi-grid kpi-grid-4 mb-16">
            <div className="kpi-tile">
              <div className="kpi-tile-label"><CloudServerOutlined /> {t('portal.services.kpi.total', 'Dienste')}</div>
              <div className="kpi-tile-value">{services.length}</div>
              <div className="kpi-tile-sub">{t('portal.services.kpi.total_sub', 'gesamt')}</div>
            </div>
            <div className={`kpi-tile ${active.length > 0 ? 'kpi-success' : ''}`}>
              <div className="kpi-tile-label">{t('portal.services.kpi.active', 'Aktiv')}</div>
              <div className={`kpi-tile-value ${active.length > 0 ? 'kpi-success' : ''}`}>{active.length}</div>
              <div className="kpi-tile-sub">{t('portal.services.kpi.active_sub', 'läuft')}</div>
            </div>
            <div className={`kpi-tile ${paused.length > 0 ? 'kpi-warning' : ''}`}>
              <div className="kpi-tile-label">{t('portal.services.kpi.paused', 'Pausiert')}</div>
              <div className="kpi-tile-value">{paused.length}</div>
              <div className="kpi-tile-sub">{t('portal.services.kpi.paused_sub', 'vorübergehend')}</div>
            </div>
            <div className={`kpi-tile ${suspended.length > 0 ? 'kpi-danger' : ''}`}>
              <div className="kpi-tile-label">{t('portal.services.kpi.suspended', 'Gesperrt')}</div>
              <div className={`kpi-tile-value ${suspended.length > 0 ? 'kpi-danger' : ''}`}>{suspended.length}</div>
              <div className="kpi-tile-sub">{t('portal.services.kpi.suspended_sub', 'bitte Rechnung prüfen')}</div>
            </div>
          </div>
        )
      })()}
      {(() => {
        const KIND_LABELS: Record<string, string> = {
          domain: t('portal.services.cat.domain', 'Domains'),
          ssl: t('portal.services.cat.ssl', 'SSL-Zertifikate'),
          license: t('portal.services.cat.license', 'Lizenzen'),
          vps: t('portal.services.cat.server', 'Server'),
          dedicated: t('portal.services.cat.server', 'Server'),
          hosting: t('portal.services.cat.hosting', 'Hosting'),
          service: t('portal.services.cat.other', 'Sonstige'),
          other: t('portal.services.cat.other', 'Sonstige'),
        }
        const kinds = Array.from(new Set((data || []).map(s => s.kind).filter(Boolean) as string[]))
        if (kinds.length < 2) return null
        return (
          <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <Tag.CheckableTag checked={catFilter === ''} onChange={() => setCatFilter('')}>
              {t('portal.services.cat.all', 'Alle')} ({(data || []).length})
            </Tag.CheckableTag>
            {kinds.map(k => (
              <Tag.CheckableTag key={k} checked={catFilter === k} onChange={() => setCatFilter(k)}>
                {KIND_LABELS[k] || k} ({(data || []).filter(s => s.kind === k).length})
              </Tag.CheckableTag>
            ))}
          </div>
        )
      })()}
      <Card className="content-card">
        <DataTable<Service>
          rowKey="subscription_id"
          columns={columns}
          dataSource={(data || []).filter(s => !catFilter || s.kind === catFilter)}
          loading={isLoading}
          searchPlaceholder={t('portal.services.searchPlaceholder', 'Suche Services')}
          renderMobileCard={renderMobileCard}
        />
      </Card>

      <Modal
        open={!!pauseTarget}
        onCancel={() => { setPauseTarget(null); setPauseUntil(null) }}
        title={t('portal.services.pauseTitle', 'Vertrag pausieren')}
        onOk={() => pause.mutate()}
        okText={t('portal.services.pauseConfirm', 'Jetzt pausieren')}
        okButtonProps={{ loading: pause.isPending }}
        width={screens.md ? 500 : '95vw'}
        destroyOnClose
      >
        <Typography.Paragraph>
          {t('portal.services.pauseHint',
            'Während der Pause wird keine Rechnung erstellt. Ihre Daten bleiben erhalten. Sie können die Pause jederzeit beenden — falls Sie ein Datum unten setzen, wird der Vertrag automatisch fortgesetzt.')}
        </Typography.Paragraph>
        <Form layout="vertical">
          <Form.Item label={t('portal.services.pauseUntil', 'Pause bis (optional)')}>
            <DatePicker
              value={pauseUntil}
              onChange={setPauseUntil}
              format="DD.MM.YYYY"
              disabledDate={(d) => d.isBefore(dayjs(), 'day')}
              className="w-full"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={!!changePlanTarget}
        onCancel={() => { setChangePlanTarget(null); setCpProductID(null); setCpCycle(''); setCpPreview(null) }}
        title={t('portal.services.changePlanTitle', 'Tarif wechseln')}
        onOk={() => applyPlanChange.mutate()}
        okText={t('portal.services.changePlanApply', 'Jetzt wechseln')}
        okButtonProps={{
          loading: applyPlanChange.isPending,
          disabled: !cpPreview || cpPreviewLoading,
        }}
        width={screens.md ? 560 : '95vw'}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary">
          {t('portal.services.changePlanHint',
            'Wählen Sie den neuen Tarif und den gewünschten Abrechnungszyklus. Der verbleibende Zeitraum Ihres aktuellen Tarifs wird als Guthaben gutgeschrieben.')}
        </Typography.Paragraph>
        <Form layout="vertical">
          <Form.Item label={t('portal.services.changePlanProduct', 'Neuer Tarif')}>
            <Select
              showSearch
              placeholder={t('portal.services.changePlanProductPlaceholder', 'Tarif auswählen')}
              value={cpProductID}
              onChange={(v) => {
                setCpProductID(v)
                setCpCycle('')
                setCpPreview(null)
              }}
              optionFilterProp="label"
              options={(catalog || [])
                .filter((p) => p.active)
                .map((p) => ({ value: p.id, label: p.name }))}
              className="w-full"
            />
          </Form.Item>
          {cpProductID && (
            <Form.Item label={t('portal.services.changePlanCycle', 'Abrechnungszyklus')}>
              <Select
                placeholder={t('portal.services.changePlanCyclePlaceholder', 'Zyklus auswählen')}
                value={cpCycle || undefined}
                onChange={(v) => {
                  setCpCycle(v)
                  if (changePlanTarget && cpProductID) {
                    void fetchPreview(changePlanTarget.subscription_id, cpProductID, v)
                  }
                }}
                options={[
                  { value: 'monthly', label: t('portalServices.cycleMonthly', 'Monatlich') },
                  { value: 'quarterly', label: t('portalServices.cycleQuarterly', 'Vierteljährlich') },
                  { value: 'yearly', label: t('portalServices.cycleYearly', 'Jährlich') },
                  { value: 'biennial', label: t('portalServices.cycleBiennial', '2-jährlich') },
                  { value: 'one_time', label: t('portalServices.cycleOneTime', 'Einmalig') },
                ]}
                className="w-full"
              />
            </Form.Item>
          )}
          {cpPreviewLoading && <Spin size="small" />}
          {cpPreview && !cpPreviewLoading && (
            <Alert
              type="info"
              showIcon
              message={
                <span>
                  {t('portal.services.changePlanNewPrice', 'Neuer Preis')}:{' '}
                  <strong>{cpPreview.new_unit_price} {cpPreview.currency}</strong>
                  {' — '}
                  {t('portal.services.changePlanNextDue', 'Nächste Abrechnung')}:{' '}
                  <strong>{new Date(cpPreview.new_next_due_date).toLocaleDateString('de-DE')}</strong>
                  {parseFloat(cpPreview.prorata_credit) > 0 && (
                    <span>
                      {' — '}
                      {t('portal.services.changePlanCredit', 'Guthaben gutgeschrieben')}:{' '}
                      <strong>{cpPreview.prorata_credit} {cpPreview.currency}</strong>
                    </span>
                  )}
                </span>
              }
            />
          )}
        </Form>
      </Modal>

      <Modal
        open={!!cancelTarget}
        onCancel={() => { setCancelTarget(null); cancelForm.resetFields() }}
        title={t('portal.services.cancelTitle', 'Kündigung einreichen')}
        onOk={() => cancelForm.submit()}
        okText={t('portal.services.cancelOk', 'Kündigung einreichen')}
        okButtonProps={{ loading: submitCancel.isPending, danger: true }}
        width={screens.md ? 520 : '95vw'}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary">
          {t('portal.services.cancelHint',
            'Ihre Kündigung muss noch vom Support bestätigt werden. Sie werden per E-Mail informiert sobald die Kündigung bearbeitet wurde.')}
        </Typography.Paragraph>
        <Form form={cancelForm} layout="vertical"
          onFinish={(vals) => submitCancel.mutate(vals)}>
          <Form.Item
            name="request_type"
            label={t('portal.services.cancelType', 'Kündigungsart')}
            rules={[{ required: true }]}
          >
            <Radio.Group>
              <Space direction="vertical">
                <Radio value="at_period_end">
                  {t('portal.services.cancelAtEnd', 'Zum Vertragsende (empfohlen)')}
                </Radio>
                <Radio value="immediate">
                  {t('portal.services.cancelImmediate', 'Sofortige Kündigung')}
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          <Form.Item name="reason" label={t('portal.services.cancelReason', 'Grund (optional)')}>
            <Input.TextArea rows={3}
              placeholder={t('portal.services.cancelReasonPlaceholder',
                'Optional: Bitte teilen Sie uns den Grund Ihrer Kündigung mit.')}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
